import { useMemo } from 'react';
import { AiOutlineDelete, AiOutlineEdit, AiOutlineEye } from 'react-icons/ai';
import { FaCheckCircle } from 'react-icons/fa';
import { FaCircleXmark } from 'react-icons/fa6';
import { TbRestore } from 'react-icons/tb';
import { Link } from 'react-router-dom';
import { ActionsIcon } from '../../../../../../assets/svg/actions';
import { IconButton } from '../../../../../../components/icon-button/icon-button';
import { InfiniteScroll } from '../../../../../../components/infinite-scroll/infinite-scroll';
import { HasAccess } from '../../../../../../features/has-access/has-access';
import { Tooltip } from '../../../../../../features/tooltip/tooltip';
import { withAllBranches } from '../../../../../../helpers/mappers';
import { buildParams, concatPath } from '../../../../../../helpers/path';
import { useLang } from '../../../../../../hooks/use-lang';
import { useModalParams } from '../../../../../../hooks/use-modal-params';
import { useQueryParams } from '../../../../../../hooks/use-query-params';
import { DevicesPath, ManagerPath, RootPath } from '../../../../../../routes/manager/manager-enums';
import { useBranchesStore } from '../../../../../../store/use-branch-store';
import { useQrStore } from '../../../../../../store/use-qr-store';
import s from './devices-qr-cards.module.css';

export const DevicesQrCards = () => {
  const { t, currentLang, getLocale } = useLang();
  const { dynamicList, isLoadingFetch } = useQrStore();
  const { place } = useBranchesStore();
  const branches = useMemo(() => withAllBranches(place, currentLang), [currentLang, place]);
  const { addQueryParams } = useQueryParams();
  const viewDynamicQrModal = useModalParams('viewDynamicQr');

  if (!dynamicList?.length && !isLoadingFetch) {
    return <div>{t('Список QR кодов пуст')}</div>;
  }

  return (
    <ul className={s.feature}>
      {dynamicList?.map((item) => {
        return (
          <li key={item.id} className={s.item}>
            <div className={s.top}>
              <div className={s.status}>
                {item.is_attached ? (
                  <FaCheckCircle size={20} color="#1A8754" />
                ) : (
                  <FaCircleXmark size={20} color="#DC3545" />
                )}

                <span>
                  <div className={s.title}>{t('Статус')}</div>
                  <div className={s.subtitle}>
                    {item.is_attached ? t('Привязан') : t('Не привязан')}
                  </div>
                </span>
              </div>

              {item.is_deleted ? (
                <HasAccess service="device" permissions={['write']}>
                  <IconButton
                    tooltip={t('Восстановить')}
                    variant="secondary"
                    size="sm"
                    onClick={() =>
                      addQueryParams({
                        restoreId: String(item.id),
                        restoreName: getLocale(item.name),
                      })
                    }
                  >
                    <TbRestore />
                  </IconButton>
                </HasAccess>
              ) : (
                <Tooltip
                  triggerWrapperClassName={s.actionsButton}
                  optionsWrapperClassName={s.actions}
                  trigger={<ActionsIcon />}
                  offset={12}
                >
                  <div className={s.actions}>
                    <IconButton
                      tooltip={t('Просмотр')}
                      variant="secondary"
                      size="sm"
                      onClick={() =>
                        viewDynamicQrModal.handleOpenModal({
                          qrId: item.id,
                          qrName: getLocale(item.name),
                          code: item.code || '',
                          isAttached: item.is_attached ? 1 : 0,
                        })
                      }
                    >
                      <AiOutlineEye />
                    </IconButton>

                    <HasAccess service="device" permissions={['write']}>
                      <Link
                        to={
                          concatPath([RootPath.Manager, ManagerPath.Devices, DevicesPath.Edit]) +
                          buildParams({ id: item.id })
                        }
                      >
                        <IconButton tooltip={t('Редактировать')} size="sm" variant="secondary">
                          <AiOutlineEdit />
                        </IconButton>
                      </Link>
                      <IconButton
                        tooltip={t('Удалить')}
                        size="sm"
                        variant="secondary"
                        onClick={() =>
                          addQueryParams({
                            deleteId: item.id,
                            deleteName: getLocale(item.name),
                          })
                        }
                      >
                        <AiOutlineDelete />
                      </IconButton>
                    </HasAccess>
                  </div>
                </Tooltip>
              )}
            </div>

            <div className={s.main}>
              <div className={s.name}>{getLocale(item.name)}</div>

              <span>
                <span className={s.title} style={{ marginRight: 17 }}>
                  {t('Описание')}
                </span>

                <span className={s.subtitle}>{getLocale(item.description)}</span>
              </span>
            </div>

            <div className={s.bottom}>
              <div className={s.title}>{t('Филиал')}</div>
              <div className={s.subtitle}>
                {branches.find((branch) => branch.id === String(item.place_id))?.value}
              </div>
            </div>
          </li>
        );
      })}

      <InfiniteScroll onEnterView={() => {}} />
    </ul>
  );
};
